import { elementFieldTypeEnums as E, getElement } from '@dashboard/services/pageFieldTypes';
import { SUPPORTED_TRIGGER_FIELDS } from '../ShowHideLogicField';

// The original Field type dropdown's order and groups: identity, address, inputs, then the rest.
const LIST_GROUP = {
  [E.EMAIL]: 'identity',
  [E.PHONE]: 'identity',
  [E.ADDRESS]: 'address',
  [E.COUNTRY]: 'address',
  [E.QUANTITY]: 'other',
  [E.TAX_ID]: 'other',
  [E.PO_NUMBER]: 'other',
};

const QUESTION_ORDER = [
  E.EMAIL,
  E.PHONE,
  E.ADDRESS,
  E.COUNTRY,
  E.TEXT,
  E.TEXTAREA,
  E.NUMBER,
  E.SELECT,
  E.MULTIPLE_CHOICE,
  E.CHECKBOX,
  E.DATE,
  E.TIME,
  E.DATE_TIME,
  E.QUANTITY,
  E.TAX_ID,
  E.PO_NUMBER,
];

const DESCRIPTIONS = {
  [E.TEXT]: 'A short answer, such as a name.',
  [E.TEXTAREA]: 'A longer answer, such as a message.',
  [E.SELECT]: 'Choose from a dropdown list.',
  [E.MULTIPLE_CHOICE]: 'Choose from a list of options.',
  [E.CHECKBOX]: 'A box to tick for agreement or consent.',
  [E.NUMBER]: 'A number with optional minimum and maximum limits.',
  [E.EMAIL]: 'An additional email address.',
  [E.PHONE]: 'A phone number with a country code.',
  [E.DATE]: 'Choose a date from a calendar.',
  [E.TIME]: 'Choose a time of day.',
  [E.DATE_TIME]: 'Choose a date and a time.',
  [E.COUNTRY]: 'Choose a country from a list.',
  [E.ADDRESS]: 'A full postal address with autocomplete.',
  [E.QUANTITY]: 'Let customers choose how many to buy.',
  [E.TAX_ID]: 'A business tax number, such as a VAT number.',
  [E.PO_NUMBER]: 'A purchase order number.',
};

const NOTES = {
  [E.EMAIL]: "The customer's main email is already included.",
};

// Single line text fields whose data type fills the customer's name in Stripe.
const NAME_ITEMS = [
  {
    id: 'name',
    label: 'Name',
    description: "The customer's full name.",
  },
  {
    id: 'first-name',
    label: 'First name',
    description: "The customer's first name.",
  },
  {
    id: 'last-name',
    label: 'Last name',
    description: "The customer's last name.",
  },
];

export const LAYOUT_ITEMS = [
  {
    id: 'group',
    kind: 'group',
    label: 'Group',
    description: 'Organize related fields and display them side by side.',
  },
  {
    id: 'heading',
    kind: 'heading',
    level: 'heading',
    label: 'Heading',
    description: 'A large title.',
  },
  {
    id: 'subheading',
    kind: 'heading',
    level: 'subheading',
    label: 'Subheading',
    description: 'A smaller title.',
  },
  {
    id: 'divider',
    kind: 'divider',
    label: 'Divider',
    description: 'A line that separates parts of the form.',
  },
];

// Not a question but the page's discount code box: checkout pages and events have one, at most.
export const discountCodeItem = ({ placed }) => ({
  id: 'discount-code',
  kind: 'discount-code',
  label: 'Discount code',
  description: 'A box where customers enter a discount code.',
  listGroup: 'checkout',
  trigger: false,
  disabledReason: placed ? 'One per page' : null,
});

// Long enough to scroll on a phone, so it stops at the screen edge.
export const PICKER_MENU_CLASS =
  'w-64 max-h-(--radix-dropdown-menu-content-available-height) overflow-y-auto';

/** Whether a subtle divider goes above the item: the list keeps the old dropdown's groups. */
export const startsListGroup = (items, index) =>
  index > 0 && items[index].listGroup !== items[index - 1].listGroup;

export const TRIGGER_NOTE = 'Can show or hide other fields.';

// Checkout pricing reads one Quantity field, so a second one would be ignored. First and last
// name are adds only: on an existing field they are its Data type, not its type.
export function pickerFields({
  allowedElements,
  fields = [],
  currentKey,
  withNames = false,
  takenReason = 'One per page',
}) {
  const allowed = new Set(allowedElements);
  const triggers = new Set(SUPPORTED_TRIGGER_FIELDS);
  const others = fields.filter(field => (field._id ?? field.key) !== currentKey);
  const quantityTaken = others.some(field => field.element === E.QUANTITY);
  const types = QUESTION_ORDER.filter(element => allowed.has(element)).map(element => ({
    id: element,
    kind: 'field',
    element,
    label: getElement(element)?.label ?? element,
    description: DESCRIPTIONS[element],
    note: NOTES[element],
    trigger: triggers.has(element),
    listGroup: LIST_GROUP[element] ?? 'inputs',
    disabledReason: element === E.QUANTITY && quantityTaken ? 'One per page' : null,
  }));
  if (!withNames || !allowed.has(E.TEXT)) return types;
  const names = NAME_ITEMS.map(({ id, label, description }) => ({
    id,
    kind: 'field',
    element: E.TEXT,
    label,
    description,
    preset: { type: id, label, placeholder: label },
    listGroup: 'identity',
    trigger: false,
    disabledReason: others.some(field => field.type === id) ? takenReason : null,
  }));
  // Name leads the list; First name and Last name follow Email and Phone.
  const [fullName, ...splitNames] = names;
  const afterIdentity = types.filter(item => [E.EMAIL, E.PHONE].includes(item.element)).length;
  return [fullName, ...types.slice(0, afterIdentity), ...splitNames, ...types.slice(afterIdentity)];
}

/** Layout items offered at a destination: groups never go inside a group. */
export function pickerLayoutItems({ groupId }) {
  return groupId ? LAYOUT_ITEMS.filter(item => item.kind !== 'group') : LAYOUT_ITEMS;
}

/**
 * The picker's sections; changing a field's type offers field types only. Attendee questions always
 * ask Name, so the Name item shows as taken.
 */
export function pickerSections({
  mode = 'add',
  groupId = null,
  allowedElements,
  fields,
  currentKey,
  discountCode = null,
  guestQuestions = false,
  layoutBlocks = true,
}) {
  const change = mode === 'change';
  return [
    {
      title: 'Fields',
      items: [
        ...pickerFields({
          allowedElements,
          fields,
          currentKey,
          withNames: !change,
          takenReason: guestQuestions ? 'One per attendee' : undefined,
        }),
        ...(discountCode && !change ? [discountCodeItem(discountCode)] : []),
      ],
    },
    { title: 'Layout', items: change || !layoutBlocks ? [] : pickerLayoutItems({ groupId }) },
  ].filter(section => section.items.length > 0);
}
